import { resolveLexiconRevisionForCacheKey } from './audioJitLexicon';

export type AudioJitProviderId = 'openai' | 'google' | 'azure';

export interface AudioJitCacheKeyInput {
  provider: AudioJitProviderId | string;
  model?: string | null;
  voice?: string | null;
  /** Course language key (e.g. en-GB), not the BCP-47 voiceLocale sent to TTS APIs. */
  locale?: string | null;
  text?: string | null;
  /** When set, appended as |lexiconRevision:N for Google/Azure lexicon cache busting. */
  lexiconRevision?: string | null;
}

export const normalizeJitCacheText = (value = '') =>
  String(value)
    .replace(/\r\n/g, '\n')
    .trim()
    .replace(/[ \t]+/g, ' ');

export const normalizeAudioJitCacheBaseUrl = (
  baseUrl?: string | null
): string =>
  String(baseUrl || '')
    .trim()
    .replace(/\/+$/g, '');

export const joinAudioJitCacheObjectUrl = (
  baseUrl: string,
  cacheKey: string
): string => `${normalizeAudioJitCacheBaseUrl(baseUrl)}/${cacheKey}.mp3`;

const toHex = (bytes: Uint8Array): string =>
  Array.from(bytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');

export const buildJitCacheKeyMaterial = ({
  provider,
  model,
  voice,
  locale,
  text,
  lexiconRevision
}: AudioJitCacheKeyInput): string => {
  const parts = [
    String(provider || '').toLowerCase(),
    String(model || '').toLowerCase(),
    String(voice || '').toLowerCase(),
    String(locale || '').toLowerCase(),
    normalizeJitCacheText(text || '')
  ];

  if (lexiconRevision) {
    parts.push(`lexiconRevision:${lexiconRevision}`);
  }

  return parts.join('|');
};

export const createJitAudioCacheKey = async ({
  provider,
  model,
  voice,
  locale,
  text,
  lexiconRevision
}: AudioJitCacheKeyInput): Promise<string> => {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error(
      'Unable to generate JiT cache key: Web Crypto API unavailable.'
    );
  }

  const input = buildJitCacheKeyMaterial({
    provider,
    model,
    voice,
    locale,
    text,
    lexiconRevision
  });

  const digest = await subtle.digest(
    'SHA-256',
    new TextEncoder().encode(input)
  );

  return toHex(new Uint8Array(digest));
};

export interface ResolveJitAudioCacheKeyInput {
  provider: AudioJitProviderId | string;
  model?: string | null;
  voice?: string | null;
  locale?: string | null;
  voiceLocale?: string | null;
  text?: string | null;
}

/** Resolves conditional lexicon revision, then hashes (client-side cache key authority). */
export const resolveJitAudioCacheKey = async (
  input: ResolveJitAudioCacheKeyInput
): Promise<string> => {
  const text = input.text ?? '';
  const lexiconRevision = input.voiceLocale
    ? await resolveLexiconRevisionForCacheKey(
        input.provider,
        input.voiceLocale,
        text
      )
    : undefined;

  return createJitAudioCacheKey({
    provider: input.provider,
    model: input.model,
    voice: input.voice,
    locale: input.locale,
    text,
    lexiconRevision
  });
};
